import { useState } from "react"
import { type ArtistoInRegia, type StatoRegia, apiRegia } from "../../api/regia"
import { useLargo } from "../../stato/useLargo"

interface Props {
  stato: StatoRegia
  codice: string
  comando: (azione: () => Promise<unknown>) => Promise<void>
}

/** Separatori accettati fra artista e canzone quando si incolla una lista.
 *  Il trattino lungo è quello che esce copiando da Wikipedia. */
const SEPARATORI = /\s+[—–-]\s+|\t|\s*;\s*/

export function parsaLista(testo: string): { name: string; song: string | null }[] {
  return testo
    .split("\n")
    .map((riga) => riga.trim())
    .filter(Boolean)
    .map((riga) => {
      const [nome, ...resto] = riga.split(SEPARATORI)
      const canzone = resto.join(" ").trim()
      // Le virgolette sopravvivono al copia-incolla più spesso di quanto sembri.
      return { name: nome.trim(), song: canzone.replace(/^["«»']|["«»']$/g, "") || null }
    })
    .filter((a) => a.name)
}

export function Artisti({ stato, codice, comando }: Props) {
  const [nome, setNome] = useState("")
  const [canzone, setCanzone] = useState("")
  const [lista, setLista] = useState("")
  const [esito, setEsito] = useState<string | null>(null)
  const largo = useLargo()

  const anteprima = parsaLista(lista)

  async function aggiungiUno(ev: React.FormEvent) {
    ev.preventDefault()
    if (!nome.trim()) return
    await comando(async () => {
      const r = await apiRegia.aggiungiArtisti(
        stato.edizione.id,
        [{ name: nome.trim(), song: canzone.trim() || null }],
        codice,
      )
      setEsito(r.inserted ? `${nome.trim()} aggiunto.` : `${nome.trim()} c'era già.`)
      setNome("")
      setCanzone("")
    })
  }

  async function incolla() {
    if (anteprima.length === 0) return
    await comando(async () => {
      const r = await apiRegia.aggiungiArtisti(stato.edizione.id, anteprima, codice)
      setEsito(
        r.saltati > 0
          ? `${r.inserted} aggiunti, ${r.saltati} già presenti.`
          : `${r.inserted} artisti aggiunti.`,
      )
      setLista("")
    })
  }

  return (
    <section className="artisti">
      <p className="occhiello">
        {stato.artisti.length} artisti in {stato.edizione.name}
      </p>

      {/* Al telefono tutto in fila; sul portatile questa è la colonna di
          destra, e l'elenco resta a sinistra a tutta altezza. */}
      <div className="artisti__laterale">
        <form className="scheda artisti__aggiungi" onSubmit={aggiungiUno}>
          <input
            className="campo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Nome dell'artista"
            aria-label="Nome dell'artista"
          />
          <input
            className="campo"
            value={canzone}
            onChange={(e) => setCanzone(e.target.value)}
            placeholder="Titolo del brano"
            aria-label="Titolo del brano"
          />
          <button className="bottone primario" type="submit" disabled={!nome.trim()}>
            Aggiungi
          </button>
        </form>

        {/* Sul portatile si apre già: è lì che si incollano i Big, una volta
            l'anno, con la pagina di Wikipedia nell'altra finestra. */}
        <details className="scheda artisti__incolla" open={largo}>
          <summary>Incolla la lista dei Big</summary>
          <p className="delicate__nota">
            Un artista per riga, con la canzone dopo un trattino: <code>Lazza — Cenere</code>. Chi
            c'è già viene saltato.
          </p>
          <textarea
            className="campo"
            rows={6}
            value={lista}
            onChange={(e) => setLista(e.target.value)}
            placeholder={"Lazza — Cenere\nTananai — Tango\nMadame — Il bene nel male"}
            aria-label="Lista da incollare"
          />
          {anteprima.length > 0 &&
            (largo ? (
              // Con lo spazio che c'è, si fa vedere come è stata letta ogni
              // riga: un trattino sbagliato si nota prima di inserire, non dopo.
              <>
                <p className="delicate__nota">
                  Riconosciuti <b>{anteprima.length}</b> artisti:
                </p>
                <ol className="artisti__anteprima">
                  {anteprima.map((a, i) => (
                    <li key={i}>
                      <span>{a.name}</span>
                      <small>{a.song ?? "senza brano"}</small>
                    </li>
                  ))}
                </ol>
              </>
            ) : (
              <p className="delicate__nota">
                Riconosciuti <b>{anteprima.length}</b> artisti. Il primo: {anteprima[0].name}
                {anteprima[0].song && ` — ${anteprima[0].song}`}.
              </p>
            ))}
          <button
            type="button"
            className="bottone primario"
            disabled={anteprima.length === 0}
            onClick={incolla}
          >
            Aggiungi {anteprima.length > 0 ? `${anteprima.length} artisti` : "la lista"}
          </button>
        </details>

        {esito && <p className="artisti__esito">{esito}</p>}
      </div>

      <ul className="artisti__elenco">
        {stato.artisti.map((a) => (
          <Voce key={a.id} artista={a} codice={codice} comando={comando} />
        ))}
      </ul>
    </section>
  )
}

function Voce({
  artista,
  codice,
  comando,
}: {
  artista: ArtistoInRegia
  codice: string
  comando: (azione: () => Promise<unknown>) => Promise<void>
}) {
  const [aperto, setAperto] = useState(false)
  const [nome, setNome] = useState(artista.name)
  const [canzone, setCanzone] = useState(artista.song ?? "")

  if (!aperto) {
    return (
      <li className="artisti__voce">
        <span className="regia__nome">
          {artista.name}
          {artista.song && <small>{artista.song}</small>}
        </span>
        <button type="button" className="bottone artisti__modifica" onClick={() => setAperto(true)}>
          Modifica
        </button>
      </li>
    )
  }

  return (
    <li className="artisti__voce artisti__voce--aperta">
      <input
        className="campo"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        aria-label="Nome"
      />
      <input
        className="campo"
        value={canzone}
        onChange={(e) => setCanzone(e.target.value)}
        placeholder="Titolo del brano"
        aria-label="Brano"
      />
      <div className="artisti__comandi">
        <button type="button" className="bottone" onClick={() => setAperto(false)}>
          Annulla
        </button>
        <button
          type="button"
          className="bottone primario"
          onClick={() =>
            comando(async () => {
              await apiRegia.modificaArtista(artista.id, { name: nome, song: canzone }, codice)
              setAperto(false)
            })
          }
        >
          Salva
        </button>
      </div>
      {/* L'eliminazione sta qui dentro e non nella riga chiusa: non deve
          essere a un tocco di distanza da chi scorre l'elenco. */}
      <button
        type="button"
        className="bottone delicate__rossa"
        onClick={() => comando(() => apiRegia.eliminaArtista(artista.id, codice))}
      >
        Elimina {artista.name}
      </button>
    </li>
  )
}
